import React, { useState, useEffect } from 'react';
import {
    FileText, Shield, Info, BookOpen, Users, Edit2, Save,
    AlertCircle, CheckCircle, Loader, PenTool, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://api.mobilink.tech/api';

const Panel = ({ children, className = "" }) => (
    <div
        className={`rounded-2xl p-5 bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}
    >
        {children}
    </div>
);

const Message = ({ type, message }) => {
    if (!message) return null;

    const isError = type === "error";
    const colors = isError
        ? {
            color: "#dc2626",
            backgroundColor: "rgba(239,68,68,0.08)",
            borderColor: "rgba(239,68,68,0.2)",
        }
        : {
            color: "#16a34a",
            backgroundColor: "rgba(34,197,94,0.08)",
            borderColor: "rgba(34,197,94,0.2)",
        };

    return (
        <div
            className="flex items-center gap-2 text-sm rounded-lg px-4 py-2.5 border"
            style={colors}
        >
            {isError ? (
                <AlertCircle size={16} className="shrink-0" />
            ) : (
                <CheckCircle size={16} className="shrink-0" />
            )}
            <span>{message}</span>
        </div>
    );
};

// Tab Navigation Component
const TabNav = ({ tabs, activeTab, onTabChange }) => {
    const iconMap = {
        'Privacy Policy': Shield,
        'Terms & Conditions': FileText,
        'About': Info,
        'User Manual': BookOpen,
        'Master Manual': Users
    };

    return (
        <div className="flex flex-wrap gap-2 p-1 bg-gray-100 rounded-xl mb-4">
            {tabs.map((tab) => {
                const Icon = iconMap[tab];
                const isActive = activeTab === tab;
                return (
                    <button
                        key={tab}
                        onClick={() => onTabChange(tab)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            isActive
                                ? 'bg-white text-orange-600 shadow-sm border border-orange-200'
                                : 'text-gray-600 hover:text-orange-500 hover:bg-orange-50'
                        }`}
                    >
                        <Icon size={16} />
                        {tab}
                        {isActive && (
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

const SectionCard = ({ 
    title, 
    icon: Icon, 
    value, 
    onSave, 
    loading, 
    saved,
    message,
    placeholder 
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value || '');

    useEffect(() => {
        setLocalValue(value || '');
    }, [value]);

    const handleSave = () => {
        onSave(localValue);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setLocalValue(value || '');
        setIsEditing(false);
    };

    // Check if this is a policy/legal section (Privacy Policy or Terms & Conditions)
    const isPolicySection = title === 'Privacy Policy' || title === 'Terms & Conditions';

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md">
                        <Icon size={16} className="text-white" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-gray-800">{title}</h2>
                        <p className="text-xs text-gray-500">
                            {isEditing ? 'Edit content below' : 'Click edit to modify'}
                        </p>
                    </div>
                </div>
                {!isEditing ? (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors border border-orange-200"
                    >
                        <Edit2 size={14} />
                        Edit
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="text-sm px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                                saved 
                                    ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white" 
                                    : "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-orange-500/30"
                            } shadow-sm`}
                        >
                            {loading ? (
                                <>
                                    <Loader size={14} className="animate-spin" />
                                    Saving...
                                </>
                            ) : saved ? (
                                <><CheckCircle size={14} /> Saved</>
                            ) : (
                                <><Save size={14} /> Save</>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <Message type={message?.type} message={message?.message} />

            {isEditing ? (
                <textarea
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    className={`w-full min-h-[280px] p-4 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none resize-y font-sans leading-relaxed ${
                        isPolicySection ? 'font-serif' : ''
                    }`}
                    placeholder={placeholder || `Enter ${title.toLowerCase()} here...`}
                    style={{
                        lineHeight: isPolicySection ? '1.8' : '1.6',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word'
                    }}
                />
            ) : (
                <div className={`p-4 rounded-xl bg-gray-50 border border-gray-200 min-h-[120px] max-h-[400px] overflow-y-auto ${
                    isPolicySection ? 'font-serif' : ''
                }`}>
                    <div 
                        className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
                        style={{
                            lineHeight: isPolicySection ? '1.8' : '1.6'
                        }}
                    >
                        {value ? (
                            value.split('\n').map((paragraph, index) => (
                                paragraph.trim() ? (
                                    <p key={index} className={isPolicySection ? 'mb-3' : 'mb-2'}>
                                        {paragraph}
                                    </p>
                                ) : null
                            ))
                        ) : (
                            <p className="text-gray-400 italic">No content available. Click Edit to add content.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const Settings = () => {
    const [activeTab, setActiveTab] = useState('Privacy Policy');
    const [settings, setSettings] = useState({
        privacyPolicy: '',
        termsAndCondition: '',
        about: '',
        userManual: '',
        masterManual: ''
    });

    const [loading, setLoading] = useState({
        privacyPolicy: false,
        termsAndCondition: false,
        about: false,
        userManual: false,
        masterManual: false
    });

    const [saved, setSaved] = useState({
        privacyPolicy: false,
        termsAndCondition: false,
        about: false,
        userManual: false,
        masterManual: false
    });

    const [messages, setMessages] = useState({
        privacyPolicy: { type: '', message: '' },
        termsAndCondition: { type: '', message: '' },
        about: { type: '', message: '' },
        userManual: { type: '', message: '' },
        masterManual: { type: '', message: '' }
    });

    const [fetchLoading, setFetchLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');

    const tabs = ['Privacy Policy', 'Terms & Conditions', 'About', 'User Manual', 'Master Manual'];

    const getToken = () => sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken');

    const fetchSettings = async () => {
        const token = getToken();
        if (!token) {
            setFetchError('No authentication token found');
            setFetchLoading(false);
            return;
        }

        setFetchLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/setting/getsettings`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const data = response.data.settings;
                setSettings({
                    privacyPolicy: data.privacyPolicy || '',
                    termsAndCondition: data.termsAndCondition || '',
                    about: data.about || '',
                    userManual: data.userManual || '',
                    masterManual: data.masterManual || ''
                });
                setFetchError('');
            } else {
                setFetchError(response.data.message || 'Failed to load settings');
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            if (error.response?.status === 401) {
                setFetchError('Session expired. Please login again.');
            } else {
                setFetchError(error.response?.data?.message || 'Failed to load settings');
            }
        } finally {
            setFetchLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleUpdate = async (key, value) => {
        const token = getToken();
        if (!token) {
            setMessages(prev => ({
                ...prev,
                [key]: { type: 'error', message: 'No authentication token found' }
            }));
            return;
        }

        const endpointMap = {
            privacyPolicy: 'updateprivacypolicy',
            termsAndCondition: 'updatetermsandcondition',
            about: 'updateabout',
            userManual: 'updateusermanual',
            masterManual: 'updatemastermanual'
        };

        const fieldMap = {
            privacyPolicy: 'privacyPolicy',
            termsAndCondition: 'termsAndCondition',
            about: 'about',
            userManual: 'userManual',
            masterManual: 'masterManual'
        };

        setLoading(prev => ({ ...prev, [key]: true }));
        setMessages(prev => ({ ...prev, [key]: { type: '', message: '' } }));
        setSaved(prev => ({ ...prev, [key]: false }));

        try {
            const response = await axios.put(
                `${API_BASE_URL}/setting/${endpointMap[key]}`,
                { [fieldMap[key]]: value },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setSettings(prev => ({ ...prev, [key]: value }));
                setSaved(prev => ({ ...prev, [key]: true }));
                setMessages(prev => ({
                    ...prev,
                    [key]: { type: 'success', message: `${key.replace(/([A-Z])/g, ' $1').trim()} updated successfully!` }
                }));

                setTimeout(() => {
                    setSaved(prev => ({ ...prev, [key]: false }));
                    setMessages(prev => ({ ...prev, [key]: { type: '', message: '' } }));
                }, 3000);
            } else {
                setMessages(prev => ({
                    ...prev,
                    [key]: { type: 'error', message: response.data.message || 'Update failed' }
                }));
            }
        } catch (error) {
            console.error(`Error updating ${key}:`, error);
            if (error.response?.status === 401) {
                setMessages(prev => ({
                    ...prev,
                    [key]: { type: 'error', message: 'Session expired. Please login again.' }
                }));
            } else {
                setMessages(prev => ({
                    ...prev,
                    [key]: { type: 'error', message: error.response?.data?.message || 'Update failed' }
                }));
            }
        } finally {
            setLoading(prev => ({ ...prev, [key]: false }));
        }
    };

    const getSectionKey = (tab) => {
        const map = {
            'Privacy Policy': 'privacyPolicy',
            'Terms & Conditions': 'termsAndCondition',
            'About': 'about',
            'User Manual': 'userManual',
            'Master Manual': 'masterManual'
        };
        return map[tab];
    };

    const getSectionIcon = (tab) => {
        const map = {
            'Privacy Policy': Shield,
            'Terms & Conditions': FileText,
            'About': Info,
            'User Manual': BookOpen,
            'Master Manual': Users
        };
        return map[tab];
    };

    const getSectionPlaceholder = (tab) => {
        const map = {
            'Privacy Policy': 'Enter your privacy policy content with proper formatting...',
            'Terms & Conditions': 'Enter your terms and conditions with proper formatting...',
            'About': 'Enter about information...',
            'User Manual': 'Enter user manual content with numbered steps...',
            'Master Manual': 'Enter master manual content with numbered steps...'
        };
        return map[tab];
    };

    if (fetchLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px] bg-gray-50 rounded-2xl">
                <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
                    <p className="text-sm text-gray-500">Loading settings...</p>
                </div>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="p-6 bg-gray-50 rounded-2xl">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600">
                    <AlertCircle size={20} />
                    <span>{fetchError}</span>
                    <button
                        onClick={fetchSettings}
                        className="ml-auto text-sm px-3 py-1.5 rounded-lg bg-red-100 hover:bg-red-200 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const currentKey = getSectionKey(activeTab);
    const currentIcon = getSectionIcon(activeTab);
    const currentPlaceholder = getSectionPlaceholder(activeTab);

    return (
        <div className="space-y-4 bg-gray-50 min-h-screen p-4 rounded-2xl">
            <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div>
                    <h1 className="text-xl font-bold text-gray-800">Content Settings</h1>
                    <p className="text-sm mt-0.5 text-gray-500">
                        Manage Privacy Policy, Terms, About, Manuals & more
                    </p>
                </div>
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-orange-50 text-orange-500 border border-orange-200">
                    <PenTool size={16} />
                    <span>{tabs.length} Sections</span>
                </div>
            </div>

            <Panel>
                <TabNav 
                    tabs={tabs} 
                    activeTab={activeTab} 
                    onTabChange={setActiveTab} 
                />

                <div className="mt-4">
                    <SectionCard
                        key={currentKey}
                        title={activeTab}
                        icon={currentIcon}
                        value={settings[currentKey]}
                        onSave={(val) => handleUpdate(currentKey, val)}
                        loading={loading[currentKey]}
                        saved={saved[currentKey]}
                        message={messages[currentKey]}
                        placeholder={currentPlaceholder}
                    />
                </div>
            </Panel>

            
        </div>
    );
};

export default Settings;